import { useClipboard } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'ui-components';
import { FEATURE_FLAGS } from '~/constants';
import { getDetailEmailFromCreator } from '~/core/utils/creator/detailedEmailTemplate';
import { getBasicEmailFromCreator } from '~/core/utils/creator/emailTemplate';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useInsightsAndBenchmarks } from '~/hooks/useInsightsAndBenchmarks';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ICreator } from '~/types';

export const useCreatorCopyEmbed = (creator: ICreator) => {
  const snackbar = useSnackbar();

  const [isCopying, setIsCopying] = useState(false);
  const manager: { id: string } = useSelector(selectTalentManager);
  const isFoamEnableAlignmentFFEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamEmbedsAlignment) === 'enabled';
  const isFoamTalentLocationFFEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamTalentLocation) === 'enabled';

  const { onCopy: onCopyEmbed, setValue } = useClipboard('', {
    format: 'text/html',
  });

  const { fetchInsightsAndBenchmarks } = useInsightsAndBenchmarks();

  const handleBasicClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const textToCopy = getBasicEmailFromCreator(
        creator,
        isFoamEnableAlignmentFFEnabled,
        isFoamTalentLocationFFEnabled,
      );
      setValue(textToCopy);
      setIsCopying(true);
    },
    [
      creator,
      setValue,
      isFoamEnableAlignmentFFEnabled,
      isFoamTalentLocationFFEnabled,
    ],
  );

  const handleDetailClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      const { insights, benchmarks } = await fetchInsightsAndBenchmarks({
        creatorIds: [creator.id],
        managerId: manager.id,
      });

      const textToCopy = getDetailEmailFromCreator(
        creator,
        isFoamEnableAlignmentFFEnabled,
        isFoamTalentLocationFFEnabled,
        benchmarks,
        insights,
      );

      setValue(textToCopy);
      setIsCopying(true);
    },
    [
      fetchInsightsAndBenchmarks,
      isFoamEnableAlignmentFFEnabled,
      isFoamTalentLocationFFEnabled,
      creator,
      manager?.id,
      setValue,
    ],
  );

  useEffect(() => {
    if (isCopying) {
      onCopyEmbed();
      setIsCopying(false);

      snackbar.success({
        isCloseable: false,
        description: 'Copied!',
        duration: 2000,
      });
    }
  }, [isCopying, onCopyEmbed, snackbar]);

  return {
    onDetailClick: handleDetailClick,
    onBasicClick: handleBasicClick,
    isCopying,
  };
};
